package com.klantenstop.web.pages.manageCompany.edit

import com.materialkobweb.components.toast.ToastManager
import com.klantenstop.web.data.models.domain.Company
import com.klantenstop.web.data.models.dto.requests.address.UpdateAddress
import com.klantenstop.web.data.models.dto.requests.address.ValidateAddressDto
import com.klantenstop.web.data.models.dto.requests.company.UpdateCompany
import com.klantenstop.web.data.repos.AddressRepo
import com.klantenstop.web.data.repos.CategoryRepo
import com.klantenstop.web.data.repos.CompanyRepo
import com.klantenstop.web.pages.manageCompany.components.AddressValidationState
import com.materialkobweb.utils.ViewModel
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext

class EditCompanyViewModel(
    private val companyId: String? = null,
    private val companyRepo: CompanyRepo = CompanyRepo,
    private val categoryRepo: CategoryRepo = CategoryRepo,
    private val addressRepo: AddressRepo = AddressRepo
) : ViewModel(), EditCompanyContract {
    private val _state = MutableStateFlow(EditCompanyContract.State())
    override val state: StateFlow<EditCompanyContract.State> = _state.asStateFlow()

    private val _effect = MutableSharedFlow<EditCompanyContract.Effect>()
    override val effect: SharedFlow<EditCompanyContract.Effect> = _effect.asSharedFlow()

    init {
        sendIntent(EditCompanyContract.Intent.LoadInitialData)
    }

    override fun sendIntent(intent: EditCompanyContract.Intent) = coroutineScope.launch {
        when (intent) {
            is EditCompanyContract.Intent.LoadInitialData -> loadInitialData()
            is EditCompanyContract.Intent.Cancel -> emitEffect(EditCompanyContract.Effect.NavigateBack())
            is EditCompanyContract.Intent.Save -> saveCompany()
            is EditCompanyContract.Intent.UpdateCompanyName -> updateState { it.copy(companyName = intent.name) }
            is EditCompanyContract.Intent.UpdateDescription -> updateState { it.copy(companyDescription = intent.description) }
            is EditCompanyContract.Intent.UpdateEmailAddress -> updateState { it.copy(emailAddress = intent.emailAddress) }
            is EditCompanyContract.Intent.UpdatePhoneNumber -> updateState { it.copy(phoneNumber = intent.phoneNumber) }
            is EditCompanyContract.Intent.UpdateWebsite -> updateState { it.copy(website = intent.website) }
            is EditCompanyContract.Intent.UpdateCategory -> updateState { it.copy(selectedCategory = intent.companyCategory) }
            is EditCompanyContract.Intent.UpdateStreetName -> updateState { it.copy(streetName = intent.streetName, addressValidationResult = null) }
            is EditCompanyContract.Intent.UpdateStreetNumber -> updateState { it.copy(streetNumber = intent.streetNumber, addressValidationResult = null) }
            is EditCompanyContract.Intent.UpdateZipCode -> updateState { it.copy(zipCode = intent.zipCode, addressValidationResult = null) }
            is EditCompanyContract.Intent.UpdateCity -> updateState { it.copy(city = intent.city, addressValidationResult = null) }
            is EditCompanyContract.Intent.UpdateCountry -> updateState { it.copy(country = intent.country, addressValidationResult = null) }
            is EditCompanyContract.Intent.ValidateAddress -> validateAddress()
        }
    }

    private suspend fun loadInitialData() = withContext(Dispatchers.Default) {
        try {
            val companyDeferred = if (!companyId.isNullOrBlank()) async { companyRepo.getCompany(companyId) } else null
            val categoriesDeferred = async { categoryRepo.getCategories() }

            val company = companyDeferred?.await()
            val categories = categoriesDeferred.await()

            updateState {
                it.copy(
                    originalCompany = company,
                    categories = categories,
                    companyName = company?.name ?: "",
                    companyDescription = company?.description ?: "",
                    phoneNumber = company?.phoneNumber ?: "",
                    emailAddress = company?.emailAddress ?: "",
                    website = company?.website?.href ?: "",
                    selectedCategory = company?.categories?.firstOrNull(),
                    // Initialize address fields from company
                    streetName = company?.address?.streetName ?: "",
                    streetNumber = company?.address?.streetNumber ?: "",
                    zipCode = company?.address?.postalCode ?: "",
                    city = company?.address?.city ?: "",
                    country = company?.address?.country ?: "",
                    addressLatitude = company?.address?.coordinates?.latitude,
                    addressLongitude = company?.address?.coordinates?.longitude
                )
            }

            if (!companyId.isNullOrBlank() && company == null) {
                ToastManager.warning("Bedrijf niet gevonden", title = "Waarschuwing")
            }
        } catch (e: Exception) {
            ToastManager.error(
                e.message ?: "Er is een fout opgetreden bij het laden van de gegevens",
                title = "Laden mislukt"
            )
        }
    }

    private suspend fun saveCompany() {
        val currentState = state.value

        // Mark that save was attempted for inline validation feedback
        updateState { it.copy(hasAttemptedSave = true, callOnRoute = true) }

        // Check if all required fields are valid (including address if changed)
        val isFormValid = currentState.isCompanyNameValid &&
                currentState.isEmailAddressValid &&
                currentState.isWebsiteValid &&
                currentState.isCategoryValid &&
                currentState.isAddressComplete &&
                currentState.isAddressValidated

        if (!isFormValid) {
            val missingFields = buildList {
                if (!currentState.isCompanyNameValid) add("bedrijfsnaam")
                if (!currentState.isEmailAddressValid) add("geldig e-mailadres")
                if (!currentState.isWebsiteValid) add("geldige website URL")
                if (!currentState.isCategoryValid) add("categorie")
                if (!currentState.isAddressComplete) add("volledig adres")
                if (!currentState.isAddressValidated) add("geverifieerd adres (indien gewijzigd)")
            }
            
            ToastManager.warning(
                "Controleer de volgende velden: ${missingFields.joinToString(", ")}",
                title = "Ongeldige invoer"
            )
            updateState { it.copy(callOnRoute = false) }
            return
        }

        try {
            val result = updateCompany()

            if (result != null) {
                val successMessage = "Bedrijf '${result.name}' succesvol bijgewerkt"
                ToastManager.success(successMessage, title = "Gelukt")
                emitEffect(EditCompanyContract.Effect.NavigateBack(result.publicIdentifier))
            } else {
                ToastManager.error("Er is een fout opgetreden bij het opslaan", title = "Opslaan mislukt")
            }
        } catch (e: Exception) {
            val errorMessage = e.message ?: "Er is een fout opgetreden bij het bijwerken van het bedrijf"
            ToastManager.error(errorMessage, title = "Opslaan mislukt")
        } finally {
            // Reset callOnRoute after handling save
            updateState { it.copy(callOnRoute = false) }
        }
    }

    private suspend fun updateCompany(): Company? {
        if (companyId.isNullOrBlank()) return null

        val currentState = state.value
        val originalAddress = currentState.originalCompany?.address
        
        // Include address in update only if address fields actually changed
        val addressChanged = currentState.streetName != originalAddress?.streetName ||
                currentState.streetNumber != originalAddress.streetNumber ||
                currentState.zipCode != originalAddress.postalCode ||
                currentState.city != originalAddress.city ||
                currentState.country != originalAddress.country
        
        val addressUpdate = if (addressChanged && currentState.isAddressComplete) {
            UpdateAddress(
                streetName = currentState.streetName,
                streetNumber = currentState.streetNumber,
                zipCode = currentState.zipCode,
                city = currentState.city,
                country = currentState.country,
                latitude = currentState.addressLatitude,
                longitude = currentState.addressLongitude
            )
        } else null
        
        val company = UpdateCompany(
            name = currentState.companyName,
            description = currentState.companyDescription,
            phoneNumber = currentState.phoneNumber,
            emailAddress = currentState.emailAddress,
            website = currentState.website,
            categories = currentState.selectedCategory?.id?.let { listOf(it) },
            address = addressUpdate
        )

        return companyRepo.updateCompany(companyId, company)
    }

    private suspend fun validateAddress() {
        val currentState = state.value

        if (!currentState.isAddressComplete) {
            ToastManager.warning(
                "Vul alle adresvelden in voordat u valideert. Kijk ook alle velden na op (spel)fouten.",
                title = "Onvolledig of fout adres"
            )
            return
        }

        updateState { it.copy(isValidatingAddress = true, addressValidationResult = null) }

        try {
            val validationRequest = ValidateAddressDto(
                streetName = currentState.streetName,
                streetNumber = currentState.streetNumber,
                zipCode = currentState.zipCode,
                city = currentState.city,
                country = currentState.country
            )

            val validationResponse = addressRepo.validateAddress(validationRequest)

            if (validationResponse == null) {
                updateState {
                    it.copy(
                        isValidatingAddress = false,
                        addressValidationResult = AddressValidationState.Error
                    )
                }
                return
            }

            if (validationResponse.valid && validationResponse.latitude != null && validationResponse.longitude != null) {
                updateState {
                    it.copy(
                        isValidatingAddress = false,
                        addressValidationResult = AddressValidationState.Success,
                        addressLatitude = validationResponse.latitude,
                        addressLongitude = validationResponse.longitude
                    )
                }
            } else {
                updateState {
                    it.copy(
                        isValidatingAddress = false,
                        addressValidationResult = AddressValidationState.Error
                    )
                }
            }
        } catch (_: Exception) {
            updateState {
                it.copy(
                    isValidatingAddress = false,
                    addressValidationResult = AddressValidationState.Error
                )
            }
        }
    }

    override fun emitEffect(effect: EditCompanyContract.Effect) = coroutineScope.launch {
        _effect.emit(effect)
    }

    override fun updateState(block: (EditCompanyContract.State) -> EditCompanyContract.State) {
        _state.update { currentState -> block(currentState) }
    }
}
